"use client";

import { useState } from "react";
import { VStack } from "@astryxdesign/core/VStack";
import { Card } from "@astryxdesign/core/Card";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { ResearchIcon } from "@/components/icons/research-icon";
import { DEFAULT_FEATURES_ITEMS } from "@/lib/landing/puck/defaults";
import { parseLandingIcon } from "@/lib/landing/puck/icon-options";
import { normalizeBackgroundImageUrl } from "@/lib/landing/media/background-image";
import { LandingCmsImage } from "@/components/landing/landing-cms-image";
import { LandingCanvasText } from "@/components/landing/landing-canvas-text";
import type { ResearchIconName } from "@/lib/icons/research-icons";
import type { LandingFeatureItem } from "@/lib/landing/puck/defaults";
import {
  asLandingTextValue,
  landingTextProps,
  styledTextString,
} from "@/lib/landing/puck/styled-text";

type LandingFeaturesGridProps = {
  layout?: "grid" | "detailed" | "tabs";
  items?: LandingFeatureItem[];
};

/** Сетка фич с настраиваемыми иконками. */
export function LandingFeaturesGrid({
  layout = "grid",
  items = DEFAULT_FEATURES_ITEMS,
}: LandingFeaturesGridProps) {
  if (layout === "tabs") {
    return <LandingFeaturesTabsPreview items={items} />;
  }

  if (layout === "detailed") {
    return <LandingFeaturesDetailed items={items} />;
  }

  return (
    <div className="@container w-full min-w-0">
      <div className="grid w-full grid-cols-1 gap-5 @[32rem]:grid-cols-2 @[56rem]:grid-cols-3">
        {items.map((feature, index) => (
          <Card
            key={`${styledTextString(feature.title)}-${styledTextString(feature.description).slice(0, 12)}`}
            padding={6}
            className="h-full min-w-0"
          >
            <VStack gap={3} align="start">
              <ResearchIcon
                name={parseLandingIcon(feature.icon) as ResearchIconName}
                size="md"
              />
              <LandingCanvasText
                field={`items.${index}.title`}
                value={feature.title}
                label="Заголовок"
              >
                <Heading level={3} className="break-words" {...landingTextProps(feature.title)} />
              </LandingCanvasText>
              <LandingCanvasText
                field={`items.${index}.description`}
                value={feature.description}
                label="Описание"
                multiline
              >
                <Text color="secondary" className="break-words" {...landingTextProps(feature.description)} />
              </LandingCanvasText>
            </VStack>
          </Card>
        ))}
      </div>
    </div>
  );
}

/** Tilda: табы + скриншот отчёта + поясняющая плитка. */
function LandingFeaturesTabsPreview({ items }: { items: LandingFeatureItem[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const safeIndex = Math.min(Math.max(0, activeIndex), items.length - 1);
  const active = items[safeIndex];

  if (!active) return null;

  const imageUrl = normalizeBackgroundImageUrl(active.imageUrl);

  return (
    <VStack gap={5} width="100%" align="start" className="min-w-0">
      <div className="-mx-1 w-[calc(100%+0.5rem)] overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex w-max min-w-full gap-4 border-b border-border sm:gap-6">
          {items.map((item, index) => {
            const isActive = index === safeIndex;
            return (
              <button
                key={styledTextString(item.title)}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={
                  isActive
                    ? "shrink-0 border-b-2 border-[var(--landing-accent,var(--color-border-accent))] pb-3 text-sm font-semibold text-[var(--color-text-primary)] transition-colors"
                    : "shrink-0 border-b-2 border-transparent pb-3 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
                }
                style={landingTextProps(item.title).style}
              >
                {landingTextProps(item.title).children}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex w-full min-w-0 flex-col gap-4 md:relative">
        <div className="overflow-hidden rounded-2xl border border-border bg-[var(--color-background-body)] shadow-sm">
          {imageUrl ? (
            <LandingCmsImage
              src={imageUrl}
              alt={styledTextString(active.title)}
              width={1200}
              height={750}
              sizes="(max-width: 768px) 100vw, 720px"
              className="block h-auto w-full max-h-[min(240px,40vh)] object-contain object-top bg-[var(--color-surface-muted)] md:max-h-[min(300px,48vh)]"
            />
          ) : (
            <div className="flex min-h-[200px] items-center justify-center bg-[var(--color-surface-muted)] px-4 sm:min-h-[280px] sm:px-6">
              <Text color="secondary" size="sm" className="text-center">
                Загрузите скриншот вкладки в поле «Скриншот»
              </Text>
            </div>
          )}
        </div>

        {/* Мобилка: карточка под превью. Desktop: плавающая поверх. */}
        <Card
          padding={5}
          className="relative z-10 w-full min-w-0 rounded-2xl border border-border bg-[var(--color-background-body)] shadow-lg md:absolute md:bottom-4 md:right-4 md:w-auto md:max-w-[min(100%,300px)]"
        >
          <VStack gap={2} align="start" width="100%" className="min-w-0">
            <LandingCanvasText
              field={`items.${safeIndex}.metric`}
              value={asLandingTextValue(active.metric)}
              label="Метрика"
              hideIfEmpty
            >
              <Text
                type="supporting"
                color="accent"
                className="uppercase tracking-wide"
                {...landingTextProps(active.metric)}
              />
            </LandingCanvasText>
            <LandingCanvasText
              field={`items.${safeIndex}.title`}
              value={active.title}
              label="Заголовок"
            >
              <Heading level={3} className="break-words" {...landingTextProps(active.title)} />
            </LandingCanvasText>
            <LandingCanvasText
              field={`items.${safeIndex}.description`}
              value={active.description}
              label="Описание"
              multiline
            >
              <Text
                color="secondary"
                size="sm"
                className="break-words"
                {...landingTextProps(active.description)}
              />
            </LandingCanvasText>
          </VStack>
        </Card>
      </div>
    </VStack>
  );
}

/** Tilda: чипы сверху + сетка 3 колонки, метрика — строка над заголовком. */
function LandingFeaturesDetailed({ items }: { items: LandingFeatureItem[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const safeIndex = Math.min(Math.max(0, activeIndex), items.length - 1);

  return (
    <VStack gap={6} width="100%" align="start" className="min-w-0">
      <div className="-mx-1 flex w-[calc(100%+0.5rem)] gap-2 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((item, index) => {
          const isActive = index === safeIndex;
          return (
            <button
              key={styledTextString(item.title)}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={
                isActive
                  ? "shrink-0 rounded-full border border-[var(--color-border-accent)] bg-[var(--color-surface-accent-muted)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)]"
                  : "shrink-0 rounded-full border border-[var(--color-border-default)] px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] hover:border-[var(--color-border-accent)] hover:text-[var(--color-text-primary)]"
              }
              style={landingTextProps(item.title).style}
            >
              {landingTextProps(item.title).children}
            </button>
          );
        })}
      </div>

      <div className="@container w-full min-w-0">
        <div className="grid w-full grid-cols-1 gap-5 @[32rem]:grid-cols-2 @[56rem]:grid-cols-3">
          {items.map((feature, index) => (
            <Card
              key={`${styledTextString(feature.title)}-${styledTextString(feature.description).slice(0, 12)}`}
              padding={6}
              className={
                index === safeIndex
                  ? "h-full min-w-0 ring-1 ring-[var(--color-border-accent)]"
                  : "h-full min-w-0"
              }
            >
              <VStack gap={2} align="start" width="100%">
                <LandingCanvasText
                  field={`items.${index}.metric`}
                  value={asLandingTextValue(feature.metric)}
                  label="Метрика"
                  hideIfEmpty
                >
                  <Text
                    type="supporting"
                    color="accent"
                    className="italic"
                    {...landingTextProps(feature.metric)}
                  />
                </LandingCanvasText>
                <ResearchIcon
                  name={parseLandingIcon(feature.icon) as ResearchIconName}
                  size="md"
                />
                <LandingCanvasText
                  field={`items.${index}.title`}
                  value={feature.title}
                  label="Заголовок"
                >
                  <Heading level={3} className="break-words" {...landingTextProps(feature.title)} />
                </LandingCanvasText>
                <LandingCanvasText
                  field={`items.${index}.description`}
                  value={feature.description}
                  label="Описание"
                  multiline
                >
                  <Text color="secondary" className="break-words" {...landingTextProps(feature.description)} />
                </LandingCanvasText>
              </VStack>
            </Card>
          ))}
        </div>
      </div>
    </VStack>
  );
}
